import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, AlertTriangle, FileText, Banknote, RotateCcw } from 'lucide-react';
import api from '../../services/api';

const fmt = (n, c) => {
  try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: c || 'USD' }).format(n || 0); }
  catch { return `${c} ${Number(n || 0).toFixed(2)}`; }
};

const STATUS_STYLE = {
  outstanding:            'bg-blue-50 text-blue-700 border-blue-200',
  overdue:                'bg-red-50 text-red-700 border-red-200',
  partially_paid:         'bg-amber-50 text-amber-700 border-amber-200',
  partially_paid_overdue: 'bg-orange-50 text-orange-700 border-orange-200',
  paid:                   'bg-emerald-50 text-emerald-700 border-emerald-200',
  draft:                  'bg-slate-100 text-slate-600 border-slate-200',
  voided:                 'bg-slate-100 text-slate-400 border-slate-200',
};

const label = (s = '') => s.replace(/_/g, ' ');

/**
 * A customer's financial position: what they owe, what they were billed, and
 * what they have paid.
 *
 * FinOps could describe an invoice's position precisely and a customer's not
 * at all — aged receivables bucketed invoices by age, and nothing answered
 * "what does this customer owe us".
 */
export default function ClientAccount({ clientId, currency }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    setError('');
    api.get(`/api/clients/${clientId}/statement`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Could not load this account'))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(8.5rem,1fr))]"
           data-testid="client-account-loading">
        {[0, 1, 2].map(i => <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3"
           data-testid="client-account-error">
        <AlertTriangle size={15} className="shrink-0 mt-0.5" /> {error}
      </div>
    );
  }

  const { balance, invoices = [], payments = [] } = data || {};
  const ccy = balance?.currencies?.length === 1 ? balance.currencies[0] : (currency || 'USD');
  const activePayments = payments.filter(p => !p.is_contra);

  return (
    <div className="space-y-4" data-testid="client-account">
      {/* ── The figures ──────────────────────────────────────────────────── */}
      {/* auto-fit rather than three fixed columns: this panel is narrow, and
          three forced columns clipped the figures -- an outstanding balance
          of US$1,000.00 rendered as "US$1". A truncated amount is not a
          cosmetic problem, it is a wrong number on the screen. */}
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(8.5rem,1fr))]">
        <Stat label="Outstanding" value={fmt(balance?.outstanding, ccy)} tone="slate"
              testid="client-outstanding" />
        <Stat label="Overdue" value={fmt(balance?.overdue, ccy)}
              tone={balance?.overdue > 0 ? 'red' : 'slate'} testid="client-overdue" />
        {/* Unapplied cash sits beside the balance rather than netted into it.
            They are different facts, and netting hides the one somebody has to
            act on: money held that has not been told what it settles. */}
        <Stat label="Unapplied cash" value={fmt(balance?.unapplied_cash, ccy)}
              tone={balance?.unapplied_cash > 0 ? 'amber' : 'slate'}
              testid="client-unapplied" />
      </div>

      {balance?.currencies?.length > 1 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2"
           data-testid="client-mixed-currency">
          This account has invoices in {balance.currencies.join(', ')}. The totals above
          add those figures together and are indicative only.
        </p>
      )}

      {balance?.identity_confidence === 'name_matched' && (
        <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2"
           data-testid="client-name-matched-warning">
          {balance.name_matched_invoice_count} of these {balance.invoice_count} invoices
          were matched to this customer by <strong>name</strong> rather than by a client
          link. A name is a spelling, not an identity — check before acting on the figure.
        </p>
      )}

      {/* ── Invoices ─────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-2">
            <FileText size={13} className="text-slate-500" /> Invoices
          </span>
          <span className="text-xs text-slate-400" data-testid="client-invoice-count">
            {invoices.length}
          </span>
        </div>
        {invoices.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-slate-400" data-testid="client-no-invoices">
            No invoices for this customer
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="client-invoices-table">
            <thead>
              <tr className="text-xs text-slate-500 border-b border-slate-100">
                <th className="text-left font-medium px-4 py-2">Invoice</th>
                <th className="text-left font-medium px-3 py-2">Issued</th>
                <th className="text-left font-medium px-3 py-2">Due</th>
                <th className="text-right font-medium px-3 py-2">Total</th>
                <th className="text-right font-medium px-3 py-2">Balance</th>
                <th className="text-left font-medium px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {invoices.slice(0, 25).map(inv => (
                <tr key={inv.invoice_id} className="hover:bg-slate-50"
                    data-testid={`client-invoice-${inv.invoice_id}`}>
                  <td className="px-4 py-2 font-mono text-xs">
                    <Link to={`/invoices/${inv.invoice_id}`}
                          className="text-indigo-700 hover:underline">
                      {inv.invoice_number || '— draft —'}
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{inv.issue_date || '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{inv.due_date || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {fmt(inv.total_amount, inv.currency_code)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">
                    {fmt(inv.balance_due_amount_placeholder, inv.currency_code)}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLE[inv.status] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                      {label(inv.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {invoices.length > 25 && (
          <p className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100">
            Showing the 25 most recent of {invoices.length}.{' '}
            <Link to={`/invoices?client_id=${clientId}`} className="text-indigo-700 hover:underline">
              See all
            </Link>
          </p>
        )}
      </div>

      {/* ── Payments ─────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-2">
            <Banknote size={13} className="text-slate-500" /> Payments
          </span>
          <span className="text-xs text-slate-400" data-testid="client-payment-count">
            {activePayments.length}
          </span>
        </div>
        {activePayments.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-slate-400" data-testid="client-no-payments">
            No payments recorded for this customer
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="client-payments-table">
            <thead>
              <tr className="text-xs text-slate-500 border-b border-slate-100">
                <th className="text-left font-medium px-4 py-2">Date</th>
                <th className="text-left font-medium px-3 py-2">Reference</th>
                <th className="text-left font-medium px-3 py-2">Method</th>
                <th className="text-right font-medium px-3 py-2">Amount</th>
                <th className="text-right font-medium px-3 py-2">Unapplied</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {activePayments.slice(0, 25).map(p => (
                <tr key={p.payment_id} className={p.is_reversed ? 'opacity-50' : ''}
                    data-testid={`client-payment-${p.payment_id}`}>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">
                    {p.payment_date || '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {p.reference || '—'}
                    {/* A reversal is part of the account's history, not
                        something to hide — the customer will remember it. */}
                    {p.is_reversed && (
                      <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700">
                        <RotateCcw size={11} /> reversed
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {label(p.payment_method) || '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {fmt(p.amount, p.currency_code)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-amber-700">
                    {p.unapplied > 0 ? fmt(p.unapplied, p.currency_code) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label: text, value, tone, testid }) {
  const tones = {
    slate: 'border-slate-200 text-slate-900',
    red:   'border-red-200 text-red-700 bg-red-50',
    amber: 'border-amber-200 text-amber-700 bg-amber-50',
  };
  return (
    <div className={`bg-white rounded-lg border px-4 py-3 min-w-0 ${tones[tone] || tones.slate}`}
         data-testid={testid}>
      <p className="text-xs text-slate-500 flex items-center gap-1.5 min-w-0"
         title={text}>
        <Wallet size={12} className="shrink-0" />
        <span className="truncate">{text}</span>
      </p>
      {/* The label may truncate; the amount may not. */}
      <p className="text-lg font-semibold font-mono mt-1 tabular-nums break-words"
         title={value}>{value}</p>
    </div>
  );
}
